// @flow
import React, { Component, Fragment } from 'react';
import LetterHead from 'components/letter/LetterHead';
import LetterContent from 'components/letter/LetterContent';
import LetterTags from 'components/letter/LetterTags';
import { BaseActions, CommonActions, DisplayActions, LettersActions } from 'store/actionCreators';
import { connect } from 'react-redux';
import type { State } from 'store';
import type { LetterData, TocItem } from 'store/modules/letters';
import LetterToc from 'components/letter/LetterToc';
import QuestionModal from 'components/common/QuestionModal/QuestionModal';
import { type ContextRouter, type Location, withRouter } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { convertToPlainText } from 'lib/common';
import ScrollToTop from 'components/common/ScrollToTop';
import LetterPlaceholder from 'components/letter/LetterPlaceholder';
import throttle from 'lodash/throttle';
import LetterLeftSticker from 'components/letter/LetterLeftSticker/LetterLeftSticker';
import LetterSeriesInfo from 'components/letter/LetterSeriesInfo/LetterSeriesInfo';

type Props = {
  username: ?string,
  urlSlug: ?string,
  letter: ?LetterData,
  toc: ?(TocItem[]),
  activeHeading: ?string,
  likeInProcess: boolean,
  currentUsername: ?string,
  askRemove: boolean,
  routerHistory: Location[],
  shouldCancel: boolean,
  logged: boolean,
} & ContextRouter;

class LetterViewer extends Component<Props> {
  onActivateHeading = throttle((headingId: string) => {
    LettersActions.activateHeading(headingId);
  }, 250);

  initialize = async () => {
    const { username, urlSlug, shouldCancel } = this.props;
    if (!username || !urlSlug) return;
    try {
      if (shouldCancel) return;
      await LettersActions.readLetter({
        username,
        urlSlug,
      });
      if (!this.props.letter) return;
      const { id } = this.props.letter;
      LettersActions.getSequences(id);
    } catch (e) {
      console.log(e);
      if (e && e.response && e.response.status === 404) {
        this.props.history.replace('/404');
      }
    }
  };

  componentDidUpdate(prevProps, prevState) {
    if (prevProps.urlSlug !== this.props.urlSlug) {
      LettersActions.unloadLetter();
      this.initialize();
    }
  }

  componentWillUnmount() {
    LettersActions.unloadLetter();
  }

  onSetToc = (toc: ?(TocItem[])) => {
    LettersActions.setToc(toc);
  };

  onToggleLike = () => {
    const { letter, likeInProcess } = this.props;
    if (likeInProcess) return;
    if (!letter) return;
    if (letter.liked) {
      LettersActions.unlike(letter.id);
    } else {
      LettersActions.like(letter.id);
    }
  };

  onToggleAskRemove = () => {
    LettersActions.toggleAskRemove();
  };

  onConfirmRemove = async () => {
    const { letter, history, routerHistory } = this.props;
    LettersActions.toggleAskRemove();
    if (!letter) return;
    try {
      await CommonActions.removeLetter(letter.id);
      DisplayActions.flushList();
    } catch (e) {
      console.log(e);
    }
    if (routerHistory.length === 0) {
      history.push('/');
      return;
    }
    history.goBack();
  };

  componentDidMount() {
    this.initialize();
    const { hash } = this.props.location;
    if (hash !== '') {
      LettersActions.activateHeading(decodeURI(hash.split('#')[1]));
    }
  }

  informCopy = () => {
    BaseActions.showToast({
      message: '링크가 복사되었습니다',
      type: 'success',
    });
  };

  render() {
    const { letter, toc, activeHeading, username, currentUsername, askRemove, logged } = this.props;
    const { onSetToc, onActivateHeading } = this;

    if (!letter) return <LetterPlaceholder />;
    if (!letter.user) return <LetterPlaceholder />;
    const plainTextBody = convertToPlainText(letter.body);

    const url = `${process.env.BASE_URL}/@${letter.user.username}/letters/${letter.url_slug}`;
    const baseTitle = `${letter.title} | 캐시바이`;
    return (
      <Fragment>
        <Helmet>
          <title>{baseTitle}</title>
          <meta name="description" content={plainTextBody} />
          <link rel="canonical" href={url} />
          <meta property="og:url" content={url} />
          <meta property="og:type" content="article" />
          <meta property="og:title" content={baseTitle} />
          <meta property="og:description" content={plainTextBody} />
          {letter.thumbnail && <meta property="og:image" content={letter.thumbnail} />}
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={baseTitle} />
          <meta name="twitter:description" content={plainTextBody} />
          {letter.thumbnail && <meta name="twitter:image" content={letter.thumbnail} />}
        </Helmet>
        <LetterLeftSticker
          logged={logged}
          likes={letter.likes}
          liked={letter.liked}
          onToggleLike={this.onToggleLike}
          url={this.props.match.url}
          title={letter.title}
          username={letter.user.username}
          informCopy={this.informCopy}
          disabled={!logged}
        />
        <LetterToc
          toc={toc}
          activeHeading={activeHeading}
          onActivateHeading={this.onActivateHeading}
        />
        <LetterHead
          id={letter.id}
          date={letter.released_at}
          title={letter.title}
          tags={letter.tags}
          categories={letter.categories}
          user={letter.user}
          likes={letter.likes}
          liked={letter.liked}
          onToggleLike={this.onToggleLike}
          ownLetter={currentUsername === username}
          onAskRemove={this.onToggleAskRemove}
          logged={logged}
          isPrivate={letter.is_private}
          url={this.props.match.url}
          informCopy={this.informCopy}
        />
        {letter.series && <LetterSeriesInfo series={letter.series} username={username || ''} />}
        <LetterContent
          thumbnail={letter.thumbnail}
          body={letter.body}
          onSetToc={onSetToc}
          onActivateHeading={onActivateHeading}
          theme={letter.meta.code_theme}
        />
        <LetterTags tags={letter.tags} />
        <QuestionModal
          open={askRemove}
          title="뉴스레터 삭제"
          description="이 뉴스레터를 정말로 삭제하시겠습니까?"
          confirmText="삭제"
          onConfirm={this.onConfirmRemove}
          onCancel={this.onToggleAskRemove}
        />
      </Fragment>
    );
  }
}

export default connect(
  ({ letters, pender, user, common }: State) => ({
    currentUsername: user.user && user.user.username,
    letter: letters.letter,
    toc: letters.toc,
    activeHeading: letters.activeHeading,
    likeInProcess: pender.pending['letters/LIKE'] || pender.pending['letters/UNLIKE'],
    askRemove: letters.askRemove,
    routerHistory: common.router.history,
    shouldCancel: common.ssr && !common.router.altered,
    logged: !!user.user,
  }),
  () => ({}),
)(withRouter(LetterViewer));



// WEBPACK FOOTER //
// ./src/containers/letter/LetterViewer.js